/**
 * Lobby Screen — Single-device mode.
 * Host enters player names and assigns them to teams via drag-and-drop.
 */

import {
  getState,
  addPlayer,
  removePlayer,
  assignTeam,
  getTeamPlayers,
  validateLobby,
  startSetup,
} from '../game-state';
import { navigate } from '../router';
import { t } from '../i18n';

let draggedPlayerId: string | null = null;

export function renderLobby(): void {
  const app = document.getElementById('app')!;
  const state = getState();
  const teamA = getTeamPlayers('A');
  const teamB = getTeamPlayers('B');
  const unassigned = state.players.filter((p) => p.team === null);
  const validationError = state.players.length >= 4 ? validateLobby() : null;

  const playerCount = state.players.length;
  const playerPlural = playerCount !== 1 ? 's' : '';

  app.innerHTML = `
    <h1>${t('gameLobby')}</h1>
    <p class="subtitle">${t('lobbySubtitle')}</p>

    <div class="stack">
      <!-- Add Player Form -->
      <div class="row" style="gap: var(--space-sm);">
        <input type="text" id="player-name-input" class="input"
          maxlength="20" placeholder="${t('playerNamePlaceholder')}" autofocus
          style="flex: 1;" />
        <button class="btn btn-primary" id="btn-add-player"
          style="width: auto; padding: var(--btn-padding-y) var(--space-lg);">${t('add')}</button>
      </div>
      <p id="add-error" class="text-muted" style="font-size: var(--font-size-xs); min-height: 1.2em;"></p>

      <!-- Player Count -->
      <p class="text-secondary center" style="font-size: var(--font-size-sm);">
        ${t('playersAdded', playerCount, playerPlural)}
        ${playerCount < 4 ? `${t('needAtLeast4')}` : ''}
      </p>

      <!-- Unassigned Players -->
      ${unassigned.length > 0 ? `
        <div class="surface" style="padding: var(--space-md);">
          <p class="text-muted" style="font-size: var(--font-size-xs); margin-bottom: var(--space-sm);">${t('unassignedDragHint')}</p>
          <div class="player-pills" id="unassigned-area">
            ${unassigned.map((p) => renderPlayerPill(p.id, p.displayName, p.isHost)).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Teams -->
      <div class="teams-grid">
        <div class="team-column" id="team-a-drop"
          data-team="A">
          <h3 style="color: var(--color-accent);">${t('teamA')}</h3>
          <p class="text-muted" style="font-size: var(--font-size-xs); margin-bottom: var(--space-sm);">
            ${teamA.length} ${teamA.length !== 1 ? t('players') : t('player')}
          </p>
          <div class="player-pills" id="team-a-pills">
            ${teamA.map((p) => renderPlayerPill(p.id, p.displayName, p.isHost)).join('')}
          </div>
          ${teamA.length === 0 ? `<p class="text-muted drop-hint">${t('dropPlayersHere')}</p>` : ''}
        </div>

        <div class="team-column" id="team-b-drop"
          data-team="B">
          <h3 style="color: var(--color-accent);">${t('teamB')}</h3>
          <p class="text-muted" style="font-size: var(--font-size-xs); margin-bottom: var(--space-sm);">
            ${teamB.length} ${teamB.length !== 1 ? t('players') : t('player')}
          </p>
          <div class="player-pills" id="team-b-pills">
            ${teamB.map((p) => renderPlayerPill(p.id, p.displayName, p.isHost)).join('')}
          </div>
          ${teamB.length === 0 ? `<p class="text-muted drop-hint">${t('dropPlayersHere')}</p>` : ''}
        </div>
      </div>

      <!-- Validation / Start -->
      ${validationError ? `<p class="error-text">${validationError}</p>` : ''}

      <button class="btn btn-primary" id="btn-start-game"
        ${validateLobby() ? 'disabled' : ''}>
        ${t('startGame')}
      </button>

      <button class="btn btn-tertiary" id="btn-back">${t('backToMenu')}</button>
    </div>
  `;

  attachLobbyListeners();
}

function renderPlayerPill(id: string, name: string, isHost: boolean): string {
  return `
    <div class="player-pill" draggable="true" data-player-id="${id}">
      <span class="pill-name">${name}${isHost ? ` <span class="badge">${t('host')}</span>` : ''}</span>
      <button class="pill-remove" data-remove-id="${id}" title="${t('removePlayer')}">&times;</button>
    </div>
  `;
}

function attachLobbyListeners(): void {
  const input = document.getElementById('player-name-input') as HTMLInputElement;
  const addBtn = document.getElementById('btn-add-player')!;
  const errorEl = document.getElementById('add-error')!;

  // Add player
  const handleAdd = () => {
    const name = input.value.trim();
    if (!name) return;
    const error = addPlayer(name, 'A'); // Default to Team A
    if (error) {
      errorEl.textContent = error;
      errorEl.style.color = 'var(--color-danger)';
    } else {
      errorEl.textContent = '';
      input.value = '';
    }
    renderLobby(); // re-render
  };

  addBtn.addEventListener('click', handleAdd);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') handleAdd();
  });

  // Remove player
  document.querySelectorAll('.pill-remove').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.removeId!;
      removePlayer(id);
      renderLobby();
    });
  });

  // Start game
  document.getElementById('btn-start-game')?.addEventListener('click', () => {
    const error = startSetup();
    if (error) {
      const errorEl = document.getElementById('add-error');
      if (errorEl) {
        errorEl.textContent = error;
        errorEl.style.color = 'var(--color-danger)';
      }
      return;
    }
    navigate('setup');
  });

  // Back
  document.getElementById('btn-back')?.addEventListener('click', () => navigate('menu'));

  // Drag and Drop
  setupDragAndDrop();
}

function setupDragAndDrop(): void {
  // Draggable pills
  document.querySelectorAll('.player-pill').forEach((pill) => {
    pill.addEventListener('dragstart', (e) => {
      draggedPlayerId = (pill as HTMLElement).dataset.playerId!;
      (pill as HTMLElement).classList.add('dragging');
      (e as DragEvent).dataTransfer!.effectAllowed = 'move';
    });

    pill.addEventListener('dragend', () => {
      (pill as HTMLElement).classList.remove('dragging');
      draggedPlayerId = null;
      // Remove all drag-over states
      document.querySelectorAll('.drag-over').forEach((el) => el.classList.remove('drag-over'));
    });

    // Touch drag support
    pill.addEventListener('touchstart', (e) => {
      draggedPlayerId = (pill as HTMLElement).dataset.playerId!;
      (pill as HTMLElement).classList.add('dragging');
    });

    pill.addEventListener('touchend', (e) => {
      if (!draggedPlayerId) return;
      const touch = (e as TouchEvent).changedTouches[0];
      const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('[data-team]') as HTMLElement | null;
      if (dropTarget) {
        const team = dropTarget.dataset.team as 'A' | 'B';
        assignTeam(draggedPlayerId, team);
      }
      (pill as HTMLElement).classList.remove('dragging');
      draggedPlayerId = null;
      renderLobby();
    });
  });

  // Drop zones
  const dropZones = document.querySelectorAll('[data-team]');
  dropZones.forEach((zone) => {
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      (e as DragEvent).dataTransfer!.dropEffect = 'move';
      (zone as HTMLElement).classList.add('drag-over');
    });

    zone.addEventListener('dragleave', () => {
      (zone as HTMLElement).classList.remove('drag-over');
    });

    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      (zone as HTMLElement).classList.remove('drag-over');
      if (draggedPlayerId) {
        const team = (zone as HTMLElement).dataset.team as 'A' | 'B';
        assignTeam(draggedPlayerId, team);
        draggedPlayerId = null;
        renderLobby();
      }
    });
  });
}
